import { useCallback, useEffect, useState } from 'react'
import { FaArrowLeft, FaRegEye } from 'react-icons/fa'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { ButtonPill } from '../../buttons/ButtonPill'
import { ButtonSecondary } from '../../buttons/ButtonSecondary'
import { EmptyState } from '../../reusable/emptyState/EmptyState'
import { StatCard, StatGrid } from '../../../ui'
import { BatchAnalysis, ViewThruBatchAnalysis } from './BatchAnalysis'
import { BatchRowDetail } from './BatchRowDetail'
import { BureauCoverageRing, bureauCoverageCountLabel } from './BureauCoverageRing'
import { FileTags, RecordTags } from './FileTags'
import {
    BATCH_SUMMARY_BUREAU_FIELDS,
    BATCH_SUMMARY_PRIMARY_FIELDS,
    BATCH_SUMMARY_SECONDARY_FIELDS,
    BatchRowRecord,
    BatchRun,
    RowStatusBadge,
    StatusBadge,
    canResumeBatch,
    downloadCompletedRowsCsv,
    isCompletedBatchRow,
} from './shared'
import styles from './runsTable.module.scss'

const BATCH_ROWS_URL = 'viewthru-batch-rows'
const RUNNING_POLL_MS = 5_000
const PAGE_SIZE = 50

interface Failure {
    title: string
    description: string
}

const describeFailure = (error: any): Failure => {
    const status = error?.response?.status
    if (status === 401 || status === 404) {
        return {
            title: 'Batch details are not available yet',
            description: `Nothing is serving ${BATCH_ROWS_URL} on this environment.`,
        }
    }
    return {
        title: 'Could not load this batch',
        description: error?.response?.data?.message || error?.message || 'The request for batch rows failed.',
    }
}

const personName = (row: BatchRowRecord) => {
    const name = `${row.firstname || ''} ${row.lastname || ''}`.trim()
    return name || '—'
}

/** Everything a person can be looked up by, so one search box covers them all. */
const rowSearchText = (row: BatchRowRecord) =>
    [
        row.firstname,
        row.lastname,
        row.email,
        row.telephone,
        row.postcode,
        row.dob,
        row.client_reference,
        row.provider_reference,
        row.row_number,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

interface BatchRowsTableProps {
    batchId: string
    /** The row already loaded by the history table, so the header renders instantly. */
    initialBatch?: BatchRun | null
    onBack: () => void
    onResume?: (run: BatchRun) => void
    resumeDisabled?: boolean
}

export const BatchRowsTable = ({
    batchId,
    initialBatch = null,
    onBack,
    onResume,
    resumeDisabled = false,
}: BatchRowsTableProps) => {
    const [batch, setBatch] = useState<BatchRun | null>(initialBatch)
    const [rows, setRows] = useState<BatchRowRecord[]>([])
    const [loading, setLoading] = useState(true)
    const [failure, setFailure] = useState<Failure | null>(null)
    const [page, setPage] = useState(1)
    const [showAllSummary, setShowAllSummary] = useState(false)
    const [analysis, setAnalysis] = useState<ViewThruBatchAnalysis | null>(null)
    const [analysisVisible, setAnalysisVisible] = useState(false)
    const [analysedAt, setAnalysedAt] = useState<Date | null>(null)
    const [analysisLoading, setAnalysisLoading] = useState(false)
    const [selectedRow, setSelectedRow] = useState<BatchRowRecord | null>(null)
    const [originDraft, setOriginDraft] = useState(initialBatch?.origin || '')
    const [originNames, setOriginNames] = useState<string[]>([])
    const [originSaving, setOriginSaving] = useState(false)
    const [search, setSearch] = useState('')

    const loadRows = useCallback(async () => {
        setLoading(true)
        try {
            const response = await api({ url: BATCH_ROWS_URL, data: { batchId } })
            if (!response.data?.success) throw new Error(response.data?.message || 'Could not load this batch.')
            setBatch(response.data.batch || null)
            if (response.data.batch?.origin) setOriginDraft(response.data.batch.origin)
            setRows(response.data.rows || [])
            setFailure(null)
        } catch (error: any) {
            setRows([])
            setFailure(describeFailure(error))
        } finally {
            setLoading(false)
        }
    }, [batchId])

    useEffect(() => {
        void loadRows()
    }, [loadRows])

    useEffect(() => {
        void (async () => {
            try {
                const response = await api({ url: 'viewthru-batch-origins', data: {} })
                const names = ((response.data?.origins || []) as { name?: string }[])
                    .map((row) => String(row.name || '').trim())
                    .filter(Boolean)
                setOriginNames(names)
            } catch {
                setOriginNames([])
            }
        })()
    }, [])

    // Rows are written when the run finishes, so keep checking while it is live.
    useEffect(() => {
        if (batch?.status !== 'running') return
        const timer = setInterval(() => {
            void loadRows()
        }, RUNNING_POLL_MS)
        return () => clearInterval(timer)
    }, [batch?.status, loadRows])

    const query = search.trim().toLowerCase()
    const filteredRows = query ? rows.filter((row) => rowSearchText(row).includes(query)) : rows
    const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))
    const currentPage = Math.min(page, pageCount)
    const pageStart = (currentPage - 1) * PAGE_SIZE
    const pageRows = filteredRows.slice(pageStart, pageStart + PAGE_SIZE)
    const completedRows = rows.filter(isCompletedBatchRow)

    // A narrowed search leaves the old page number out of range otherwise.
    useEffect(() => {
        setPage(1)
    }, [query])

    const goToPage = (next: number) => setPage(Math.min(pageCount, Math.max(1, next)))

    const saveOrigin = async () => {
        const originName = originDraft.trim()
        if (originSaving || !originName || originName === (batch?.origin || '')) return
        setOriginSaving(true)
        try {
            const response = await api({ url: 'viewthru-batch-set-origin', data: { batchId, origin: originName } })
            if (!response.data?.success) {
                throw new Error(response.data?.message || 'Could not set the origin.')
            }
            const updated = response.data.batch as BatchRun
            setBatch((current) => (current ? { ...current, ...updated } : updated))
            setOriginDraft(updated.origin || originName)
            setOriginNames((names) =>
                names.includes(originName) ? names : [...names, originName].sort((a, b) => a.localeCompare(b))
            )
        } catch (error: any) {
            Report.failure(
                'Could not set origin',
                error?.response?.data?.message || error?.message || 'The origin was not saved.',
                'Okay'
            )
        } finally {
            setOriginSaving(false)
        }
    }

    const downloadCompletedCsv = () => {
        const count = downloadCompletedRowsCsv(rows, batch?.filename || batchId)
        if (!count) {
            Report.info('Nothing to download', 'No rows are saved for this batch yet.', 'Okay')
        }
    }

    const runAnalysis = useCallback(async () => {
        setAnalysisLoading(true)
        try {
            const response = await api({ url: 'viewthru-batch-analysis', data: { batchId } })
            if (!response.data?.success) {
                throw new Error(response.data?.message || 'Could not analyse this batch.')
            }
            setAnalysis(response.data)
            if (response.data.batch) {
                setBatch((current) => (current ? { ...current, ...response.data.batch } : response.data.batch))
            }
            setAnalysedAt(new Date())
            setAnalysisVisible(true)
        } catch (error: any) {
            Report.failure(
                'Analysis failed',
                error?.response?.data?.message || error?.message || 'Could not analyse this batch.',
                'Okay'
            )
        } finally {
            setAnalysisLoading(false)
        }
    }, [batchId])

    /** Only the first press runs the analysis; after that this just hides and shows it. */
    const toggleAnalysis = () => {
        if (analysisLoading) return
        if (analysis) {
            setAnalysisVisible((visible) => !visible)
            return
        }
        void runAnalysis()
    }

    const analysisButtonTitle = analysisLoading
        ? 'Analysing...'
        : !analysis
          ? 'Analyse batch'
          : analysisVisible
            ? 'Hide analysis'
            : 'Show analysis'

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <ButtonSecondary onClick={onBack}>
                    <span className={styles.backButtonInner}>
                        <FaArrowLeft /> Back to batches
                    </span>
                </ButtonSecondary>
                <h2>{batch?.filename || 'Batch details'}</h2>
                {batch && <StatusBadge status={batch.status} />}
                <div className={styles.headerActions}>
                    {batch && batch.status !== 'running' && completedRows.length > 0 && (
                        <ButtonPill
                            title={analysisButtonTitle}
                            colour="appleBlue"
                            onClick={toggleAnalysis}
                            disabled={analysisLoading}
                        />
                    )}
                    {batch && onResume && canResumeBatch(batch) && (
                        <ButtonPill
                            title="Resume remaining"
                            colour="appleBlue"
                            onClick={() => onResume(batch)}
                            disabled={resumeDisabled}
                        />
                    )}
                    {completedRows.length > 0 && (
                        <ButtonPill
                            title="Download CSV"
                            colour="appleBlue"
                            onClick={downloadCompletedCsv}
                        />
                    )}
                </div>
            </div>

            {batch && (
                <>
                    <label className={styles.originEditor}>
                        <span className={styles.detailLabel}>Origin</span>
                        <span className={styles.originEditorRow}>
                            <input
                                list="viewthru-batch-detail-origins"
                                value={originDraft}
                                onChange={(event) => setOriginDraft(event.target.value)}
                                placeholder="Name where this data came from"
                                autoComplete="off"
                                disabled={originSaving}
                            />
                            <datalist id="viewthru-batch-detail-origins">
                                {originNames.map((name) => (
                                    <option key={name} value={name} />
                                ))}
                            </datalist>
                            <button
                                type="button"
                                onClick={() => void saveOrigin()}
                                disabled={
                                    originSaving ||
                                    !originDraft.trim() ||
                                    originDraft.trim() === (batch.origin || '')
                                }
                            >
                                {originSaving ? 'Saving...' : batch.origin ? 'Update' : 'Set origin'}
                            </button>
                        </span>
                    </label>
                    {batch.error_message && (
                        <p className={`${styles.detailError} ${styles.summaryError}`}>{batch.error_message}</p>
                    )}
                    <StatGrid minColumn={160} className={styles.summaryGrid}>
                        {BATCH_SUMMARY_BUREAU_FIELDS.map((field) => (
                            <StatCard
                                key={field.label}
                                label={field.label}
                                format={false}
                                value={
                                    <span className={styles.bureauStatValue}>
                                        <BureauCoverageRing
                                            label={field.label}
                                            bureauCount={batch[field.key]}
                                            completeCount={batch.success_count}
                                            variant="stat"
                                        />
                                    </span>
                                }
                                sub={bureauCoverageCountLabel(batch[field.key], batch.success_count)}
                            />
                        ))}
                        {(showAllSummary
                            ? [...BATCH_SUMMARY_PRIMARY_FIELDS, ...BATCH_SUMMARY_SECONDARY_FIELDS]
                            : BATCH_SUMMARY_PRIMARY_FIELDS
                        ).map((field) => (
                            <StatCard
                                key={field.label}
                                label={field.label}
                                value={field.value(batch)}
                                format={false}
                            />
                        ))}
                    </StatGrid>
                    <button
                        type="button"
                        className={styles.summaryToggle}
                        onClick={() => setShowAllSummary((open) => !open)}
                        aria-expanded={showAllSummary}
                    >
                        {showAllSummary
                            ? 'Show less'
                            : `Show ${BATCH_SUMMARY_SECONDARY_FIELDS.length} more details`}
                    </button>
                </>
            )}

            {analysis && (
                // Kept mounted while hidden so the filters and expanded clients survive a collapse.
                <div className={analysisVisible ? undefined : styles.collapsedBlock}>
                    <BatchAnalysis
                        analysis={analysis}
                        batchId={batchId}
                        analysedAt={analysedAt}
                        filenameBase={batch?.filename || batchId}
                        onRerun={runAnalysis}
                        rerunning={analysisLoading}
                        onAnalysisRefresh={runAnalysis}
                    />
                </div>
            )}

            {rows.length > 0 && (
                <label className={styles.rowSearch}>
                    <span className={styles.detailLabel}>Find a person</span>
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Email, name, phone, postcode, client or provider reference"
                        autoComplete="off"
                    />
                    {query && (
                        <span className={styles.stackedMeta}>
                            {filteredRows.length} of {rows.length} {filteredRows.length === 1 ? 'row' : 'rows'}
                        </span>
                    )}
                </label>
            )}

            <div className={styles.tableWrapper}>
                {failure ? (
                    <EmptyState
                        bare
                        tone="error"
                        title={failure.title}
                        description={failure.description}
                        action={<ButtonSecondary onClick={loadRows}>Try again</ButtonSecondary>}
                    />
                ) : loading && rows.length === 0 ? (
                    <EmptyState bare tone="loading" title="Loading batch rows" />
                ) : rows.length === 0 ? (
                    <EmptyState
                        bare
                        tone={batch?.status === 'running' ? 'loading' : undefined}
                        title={batch?.status === 'running' ? 'Batch still running' : 'No rows saved for this batch'}
                        description={
                            batch?.status === 'running'
                                ? 'People appear here as soon as the file is accepted. This page refreshes itself.'
                                : 'This batch finished without writing any per-person rows.'
                        }
                    />
                ) : filteredRows.length === 0 ? (
                    <EmptyState
                        bare
                        title="No one in this batch matches that search"
                        description={`Nothing matched "${search.trim()}". Emails are only held for people whose spreadsheet row included one.`}
                        action={<ButtonSecondary onClick={() => setSearch('')}>Clear search</ButtonSecondary>}
                    />
                ) : (
                    <table className={styles.runsTable}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Person</th>
                                <th>Status</th>
                                <th>Files</th>
                                <th>Records</th>
                                <th aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.row_number}</td>
                                    <td className={styles.stackedCell} title={row.postcode || ''}>
                                        <span>{personName(row)}</span>
                                        <span className={styles.stackedMeta}>
                                            {row.dob || 'No date of birth'}
                                            {row.client_reference ? ` · ${row.client_reference}` : ''}
                                        </span>
                                        <span className={styles.stackedMeta} title={row.email || ''}>
                                            {row.email || 'No email'}
                                        </span>
                                    </td>
                                    <td className={styles.stackedCell}>
                                        <RowStatusBadge row={row} />
                                        <span
                                            className={styles.stackedMeta}
                                            title={row.provider_reference || ''}
                                        >
                                            {row.provider_reference || '—'}
                                        </span>
                                    </td>
                                    <td className={styles.tagCell}>
                                        <FileTags row={row} />
                                    </td>
                                    <td className={styles.tagCell}>
                                        <RecordTags row={row} />
                                    </td>
                                    <td className={styles.actionsCell}>
                                        <button
                                            type="button"
                                            className={styles.iconButton}
                                            title="View full details"
                                            aria-label={`View details for row ${row.row_number}`}
                                            onClick={() => setSelectedRow(row)}
                                        >
                                            <FaRegEye />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {filteredRows.length > 0 && (
                <div className={styles.paginationBar}>
                    <p className={styles.tableFootnote}>
                        Showing {pageStart + 1}–{pageStart + pageRows.length} of {filteredRows.length}
                        {query && ` matching (${rows.length} in the batch)`}
                    </p>
                    {pageCount > 1 && (
                        <div className={styles.paginationControls}>
                            <button
                                className={styles.pageButton}
                                type="button"
                                onClick={() => goToPage(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            {Array.from({ length: pageCount }, (_, index) => index + 1)
                                .filter(
                                    (pageNumber) =>
                                        pageNumber === 1 ||
                                        pageNumber === pageCount ||
                                        (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                                )
                                .map((pageNumber, index, visiblePages) => (
                                    <span key={pageNumber} className={styles.pageNumberGroup}>
                                        {index > 0 && visiblePages[index - 1] !== pageNumber - 1 && (
                                            <span className={styles.pageEllipsis}>…</span>
                                        )}
                                        <button
                                            className={`${styles.pageButton} ${
                                                pageNumber === currentPage ? styles.pageButtonActive : ''
                                            }`}
                                            type="button"
                                            onClick={() => goToPage(pageNumber)}
                                            aria-current={pageNumber === currentPage ? 'page' : undefined}
                                        >
                                            {pageNumber}
                                        </button>
                                    </span>
                                ))}
                            <button
                                className={styles.pageButton}
                                type="button"
                                onClick={() => goToPage(currentPage + 1)}
                                disabled={currentPage === pageCount}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            {selectedRow && (
                <BatchRowDetail
                    row={selectedRow}
                    onClose={() => setSelectedRow(null)}
                    onRowUpdated={(patch) => {
                        setRows((existing) =>
                            existing.map((item) => (item.id === patch.id ? { ...item, ...patch } : item))
                        )
                        setSelectedRow((item) => (item && item.id === patch.id ? { ...item, ...patch } : item))
                    }}
                />
            )}
        </div>
    )
}
